import { inject, Injectable } from "@angular/core";
import { first, map, Observable } from "rxjs";
import { GraphService } from "@kernel/graphql/services/graph.service";
import { IDayRoundingSettings } from "../interfaces/day-rounding-settings.interface";
import { IDriverEligibilitySettings } from "../interfaces/driver-eligibility-settings.interface";
import { DAY_ROUNDING_SETTINGS_QUERY } from "../queries/day-rounding-settings.query";
import { DRIVER_ELIGIBILITY_SETTINGS_QUERY } from "../queries/driver-eligibility-settings.query";

/**
 * GraphQL reads of the general settings (Day Rounding and Driver Eligibility). Provided on the domain route, not in
 * root. The saves go through the form mutation configs.
 */
@Injectable()
export class GeneralSettingsApiService {
  /** GraphQL service used for executing queries. */
  private readonly graphService = inject(GraphService);

  /**
   * Fetches the Day Rounding settings, or their defaults before the first save.
   *
   * @returns An observable that emits the Day Rounding settings.
   * @throws GraphQL error if the response contains errors.
   */
  getDayRoundingSettings(): Observable<IDayRoundingSettings> {
    return this.graphService
      .constructQuery<{ getDayRoundingSettings: IDayRoundingSettings }>(DAY_ROUNDING_SETTINGS_QUERY, "getDayRoundingSettings")
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data.getDayRoundingSettings;
        })
      );
  }

  /**
   * Fetches the Driver Eligibility settings, or their defaults before the first save.
   *
   * @returns An observable that emits the Driver Eligibility settings.
   * @throws GraphQL error if the response contains errors.
   */
  getDriverEligibilitySettings(): Observable<IDriverEligibilitySettings> {
    return this.graphService
      .constructQuery<{ getDriverEligibilitySettings: IDriverEligibilitySettings }>(DRIVER_ELIGIBILITY_SETTINGS_QUERY, "getDriverEligibilitySettings")
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data.getDriverEligibilitySettings;
        })
      );
  }
}
